import { createAsyncThunk } from "@reduxjs/toolkit"

import { errorSave } from "../../../redux/errors.slice"
import { type BasePagination } from "../../../types/api.ts"
import { showResponseMessage } from "../../../utils/handlers/show-response-message.ts"
import * as services from "../api/album.services"
import type { AlbumListParams } from "../api/services.types.ts"
import type {
  Album,
  AlbumCreateData,
  AlbumDeleteFilesMode,
  AlbumFilterOptions,
  AlbumListItem,
  AlbumUpdateData
} from "../types/album.types.ts"
import { albumSliceName } from "./album.constants.ts"
import { type AlbumFilters } from "./album.types.ts"

// GET /project-management/albums/
export const fetchAlbums = createAsyncThunk<
  BasePagination<AlbumListItem>,
  AlbumListParams | void
>(
  `${albumSliceName}/getAlbums`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getAlbums(params || undefined)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    return response.data as BasePagination<AlbumListItem>
  }
)

export const fetchMoreAlbums = createAsyncThunk<
  BasePagination<AlbumListItem>,
  AlbumListParams | void
>(
  `${albumSliceName}/fetchMoreAlbums`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getAlbums(params || undefined)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    return response.data as BasePagination<AlbumListItem>
  }
)

// POST /project-management/albums/
export const createAlbum = createAsyncThunk<AlbumListItem, AlbumCreateData>(
  `${albumSliceName}/createAlbum`,
  async (data, { dispatch, rejectWithValue }) => {
    const response = await services.createAlbum(data)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    showResponseMessage(response, "Album created successfully.")

    return response.data?.details?.data as AlbumListItem
  }
)

// GET /project-management/albums/{id}/
export const getAlbum = createAsyncThunk<
  Album,
  { id: string; filters: AlbumFilters }
>(
  `${albumSliceName}/getAlbum`,
  async ({ id, filters }, { dispatch, rejectWithValue }) => {
    const response = await services.getAlbum(id, filters)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    return response.data as Album
  }
)

// PUT /project-management/albums/{id}/
export const updateAlbum = createAsyncThunk<
  Album,
  { id: number | string; data: AlbumUpdateData }
>(
  `${albumSliceName}/updateAlbum`,
  async ({ id, data }, { dispatch, rejectWithValue }) => {
    const response = await services.updateAlbum(id, data)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    showResponseMessage(response, "Album updated successfully.")

    return response.data?.details?.data as Album
  }
)

// PATCH /project-management/albums/{id}/
export const patchAlbum = createAsyncThunk<
  Album,
  { id: number | string; data: Partial<AlbumUpdateData> }
>(
  `${albumSliceName}/patchAlbum`,
  async ({ id, data }, { dispatch, rejectWithValue }) => {
    const response = await services.patchAlbum(id, data)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    showResponseMessage(response, "Album updated successfully.")

    return response.data?.details?.data as Album
  }
)

// DELETE /project-management/albums/{id}/
// Omitting `files` means "keep them" — the safe option, and what the backend
// assumes when the parameter is absent.
export const deleteAlbum = createAsyncThunk<
  string | number,
  { id: number | string; files?: AlbumDeleteFilesMode }
>(
  `${albumSliceName}/deleteAlbum`,
  async ({ id, files }, { dispatch, rejectWithValue }) => {
    const response = await services.deleteAlbum(id, files)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    showResponseMessage(response, "Album deleted successfully.")
    return id
  }
)

// POST /project-management/albums/{id}/add-attachments/
export const addAlbumAttachments = createAsyncThunk<
  Album,
  { id: number | string; data: { attachment_ids: string[] } }
>(
  `${albumSliceName}/addAlbumAttachments`,
  async ({ id, data }, { dispatch, rejectWithValue }) => {
    const response = await services.addAlbumAttachments(id, data)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    showResponseMessage(response, "Attachments added successfully.")

    return response.data?.details?.data as Album
  }
)

// POST /project-management/albums/{id}/remove-attachments/
export const removeAlbumAttachments = createAsyncThunk<
  Album,
  { id: number | string; data: { attachment_ids: string[] } }
>(
  `${albumSliceName}/removeAlbumAttachments`,
  async ({ id, data }, { dispatch, rejectWithValue }) => {
    const response = await services.removeAlbumAttachments(id, data)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    showResponseMessage(response, "Attachments removed successfully.")

    return response.data?.details?.data as Album
  }
)

export const fetchAlbumFilterOptions = createAsyncThunk<
  AlbumFilterOptions,
  string
>(
  `${albumSliceName}/fetchAlbumFilterOptions`,
  async (id, { dispatch, rejectWithValue }) => {
    const response = await services.getAlbumFilterOptions(id)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    return response.data as AlbumFilterOptions
  }
)
